// hooks/settings/useAppearance.js
import { useCallback, useEffect, useState } from "react";
import { axios, errorResponse } from "../../helpers";
import { useSelector } from "react-redux";
import { loginData } from "../../stores/auth";

const DEFAULT_APPEARANCE = {
  hero: false,
  howItWorks: false,
  ourProduct: false,
  customerReviews: false,
  ourClients: false,
  subsNewsletter: false,
  socialMedia: false,
  allProductSitemap: false,
};

const useAppearance = () => {
  const [appearance, setAppearance] = useState(DEFAULT_APPEARANCE);
  const dataLogin = useSelector(loginData);
  let token = dataLogin.data.token;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  
  const fetchAppearanceSettings = useCallback(async () => {
    if (!token) {
      setIsLoading(false);
      setAppearance(DEFAULT_APPEARANCE);
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const response = await axios.get("/v1/appearance", {
        headers: { Authorization: `Bearer ${token}` },
      });

      

      if (response.data.success) {
        const apiData = response.data.data;

        // LOGIC UNTUK MENGOLAH DATA DARI BACKEND
        if (apiData && typeof apiData === "object") {
          // Kasus 1: Data langsung di root
          if (apiData.hero !== undefined) {
            setAppearance({
              ...DEFAULT_APPEARANCE,
              ...apiData,
            });
          }
          // Kasus 2: Data di dalam sectionVisibility
          else if (apiData.sectionVisibility) {
            setAppearance({
              ...DEFAULT_APPEARANCE,
              ...apiData.sectionVisibility,
            });
          }
          // Kasus 3: Data adalah array atau format lain
          else {
            console.warn("Unexpected data structure:", apiData);
            setAppearance(DEFAULT_APPEARANCE);
          }
        } else {
          setAppearance(DEFAULT_APPEARANCE);
        }
      } else {
        setError(response.data.message || "Failed to fetch settings");
        setAppearance(DEFAULT_APPEARANCE);
      }
    } catch (err) {
      const msg = errorResponse(err) || "Error fetching appearance settings";
      setError(msg);
      setAppearance(DEFAULT_APPEARANCE);
      console.error("Fetch error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAppearanceSettings();
  }, [fetchAppearanceSettings]);

  // Fungsi untuk toggle single section
  const toggleSection = useCallback(
    async (sectionName, value) => {
      if (!token) return { success: false, message: "No token" };

      try {
        // OPTIMISTIC UPDATE: langsung update UI
        setAppearance((prev) => ({
          ...prev,
          [sectionName]: value,
        }));

        // Kirim ke backend
        const response = await axios.put(
          "/v1/appearance",
          {
            sectionVisibility: {
              [sectionName]: value,
            },
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.success) {
          return {
            success: true,
            data: response.data,
            message: "Updated successfully",
          };
        } else {
          // ROLLBACK jika gagal
          setAppearance((prev) => ({
            ...prev,
            [sectionName]: !value, // Kembalikan ke nilai sebelumnya
          }));
          return {
            success: false,
            message: response.data.message || "Update failed",
          };
        }
      } catch (err) {
        // ROLLBACK jika error
        setAppearance((prev) => ({
          ...prev,
          [sectionName]: !value,
        }));

        const msg = errorResponse(err) || "Error updating section";
        return {
          success: false,
          message: msg,
        };
      }
    },
    [token]
  );

  // Fungsi untuk update semua sekaligus
  const updateAppearance = useCallback(
    async (newAppearance) => {
      if (!token) return { success: false, message: "No token" };

      try {
        const response = await axios.put(
          "/v1/appearance",
          { sectionVisibility: newAppearance },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.success) {
          setAppearance(newAppearance);
          return {
            success: true,
            data: response.data,
            message: "All settings updated",
          };
        }
        return {
          success: false,
          message: response.data.message || "Update failed",
        };
      } catch (err) {
        const msg = errorResponse(err) || "Error updating settings";
        return {
          success: false,
          message: msg,
        };
      }
    },
    [token]
  );

  return {
    appearance,
    isLoading,
    error,
    refetch: fetchAppearanceSettings,
    updateAppearance,
    toggleSection,
    setAppearance, // tambahkan jika perlu manual update
  };
};

export default useAppearance;
